import { useTranslation } from "react-i18next";
import { RiArrowRightSLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

const lockIcon = "/images/privacy/lock.svg";
const shieldIcon = "/images/privacy/shield.svg";
const layoutMasonryIcon = "/images/privacy/layout-masonry.svg";

const featureData = [
  {
    icon: lockIcon,
    titleKey: "privacy.items.privacy_title",
    descKey: "privacy.items.privacy_desc",
  },
  {
    icon: shieldIcon,
    titleKey: "privacy.items.safety_title",
    descKey: "privacy.items.safety_desc",
    iconClassName: "md:-bottom-[15px]",
  },
  {
    icon: layoutMasonryIcon,
    titleKey: "privacy.items.design_title",
    descKey: "privacy.items.design_desc",
    iconClassName: "md:-right-[3px] md:-bottom-[21px]",
  },
];

export default function DataPrivacy() {
  const { t } = useTranslation("home");
  const ctaLabel = t("privacy.cta_label");

  return (
    <section className="bg-figma-background-surface-alternates-bg-secondary">
      <div className="wrap">
        <PageSection>
          <h2 className="font-display text-figma-text-headers-secondary md:leading-display text-figma-titles-sm-size md:text-figma-titles-xl-size text-center leading-[1.15] font-extrabold">
            {t("privacy.headline")}
          </h2>
          <FeatureGrid>
            {featureData.map((feature) => (
              <FeatureCard
                key={feature.titleKey}
                sid={feature.titleKey}
                icon={feature.icon}
                iconClassName={feature.iconClassName}
                title={t(feature.titleKey)}
                description={t(feature.descKey)}
                ctaLabel={ctaLabel}
              />
            ))}
          </FeatureGrid>
        </PageSection>
      </div>
    </section>
  );
}

//=========================================== Components

function PageSection({ children }: { children: React.ReactNode }) {
  return (
    <div className="sm:px-figma-padding-padding-l-r-pl-r-48 xl:px-figma-padding-padding-l-r-pl-r-56 flex flex-col items-start self-stretch md:px-18 lg:px-0">
      <div className="gap-figma-spacing-xl py-figma-padding-padding-t-b-pt-b-40 flex flex-col items-center self-stretch md:gap-10">
        {children}
      </div>
    </div>
  );
}

function FeatureGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="gap-figma-spacing-lg md:gap-figma-spacing-xl grid w-full grid-cols-1 md:grid-cols-[repeat(3,280px)] md:justify-center">
      {children}
    </div>
  );
}

function FeatureCard({
  icon,
  iconClassName,
  title,
  description,
  ctaLabel,
  sid,
}: {
  sid: string;
  icon: string;
  iconClassName?: string;
  title: string;
  description: string;
  ctaLabel: string;
}) {
  // Figma fixes cards at 184px (mobile) / 220px (desktop); min-h lets longer translations still grow.
  return (
    <div className="bg-figma-background-surface-bg-primary gap-figma-spacing-md rounded-figma-radius-radius-2xl px-figma-padding-padding-l-r-pl-r-24 py-figma-padding-padding-t-b-pt-b-24 md:px-figma-padding-padding-l-r-pl-r-32 md:py-figma-padding-padding-t-b-pt-b-32 relative flex h-full min-h-[184px] flex-col items-start overflow-hidden shadow-[0px_4px_8px_0px_rgba(241,241,241,0.25)] md:min-h-[220px]">
      <div className="gap-figma-spacing-xs relative z-[1] flex w-full flex-col items-start">
        <p className="text-figma-text-headers-primary md:text-figma-body-l-size text-figma-body-md-size leading-5 font-bold tracking-[0.16px] md:leading-[1.1] md:tracking-[0.18px]">{title}</p>
        <p className="text-figma-text-body-secondary-alt text-figma-body-sm-size leading-5">{description}</p>
      </div>
      <Button data-sid={`data-privacy-cta:${sid}`}
        variant="link"
        size="sm"
        className="text-figma-body-md-size relative z-[1] h-auto gap-1.25 px-0 py-0 leading-5 underline"
        iconRight={<RiArrowRightSLine className="size-5" />}
      >
        {ctaLabel}
      </Button>
      <img
        // eslint-disable-next-line local/no-raw-img -- always one of the privacy/*.svg icons
        src={icon}
        alt=""
        aria-hidden
        className={cn(
          "pointer-events-none absolute right-0 -bottom-[26px] size-[147px] md:-right-[7px] md:-bottom-4",
          iconClassName,
        )}
      />
    </div>
  );
}
